import React, { useState, useEffect, useContext } from "react";
import { useSelector } from "react-redux";
import { Typography, TextField, Button, Grid, Card, Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel, Box, Divider } from "@mui/material";
import { FirebaseContext } from 'common';
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import CircularLoading from "components/CircularLoading";
import GoBackButton from "components/GoBackButton";
import MaterialTable from "material-table";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import {
  WORKSHOP_BUSINESS_TYPES,
  VEHICLE_SOLUTION_TYPES,
  getWorkshop,
  saveWorkshop,
  subscribeWorkshopSolutions,
  saveWorkshopSolution,
  deleteWorkshopSolution,
  uploadWorkshopImage,
  solutionTypeLabel,
  businessTypeLabel,
} from '../common/workshopService';

export default function EditWorkshop() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const { database, storage } = useContext(FirebaseContext);
  const navigate = useNavigate();
  const { userId, id } = useParams();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [data, setData] = useState({
    name: '',
    businessType: 'mechanic',
    phone: '',
    description: '',
    address: '',
    lat: '',
    lng: '',
    servicesOffered: [],
    image: null,
    active: true,
  });
  const [solutions, setSolutions] = useState([]);
  const [showSolutionForm, setShowSolutionForm] = useState(false);
  const [solution, setSolution] = useState({
    solutionType: 'other',
    title: '',
    vehiclePlate: '',
    vehicleMake: '',
    cost: '',
    notes: '',
  });

  useEffect(() => {
    if (isNew) return;
    let alive = true;
    getWorkshop(database, userId, id).then((ws) => {
      if (!alive) return;
      if (ws) {
        setData({
          ...ws,
          lat: ws.lat ?? '',
          lng: ws.lng ?? '',
          servicesOffered: ws.servicesOffered || [],
        });
      }
      setLoading(false);
    }).catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [database, userId, id, isNew]);

  useEffect(() => {
    if (isNew) return;
    return subscribeWorkshopSolutions(database, userId, id, setSolutions);
  }, [database, userId, id, isNew]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setData((d) => ({ ...d, [field]: value }));
  };

  const parseCoord = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  const handleSubmit = async () => {
    if (!data.name || !String(data.name).trim()) {
      alert(t('workshop_name_required', { defaultValue: 'El nombre del taller es obligatorio' }));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...data,
        name: String(data.name).trim(),
        lat: parseCoord(data.lat),
        lng: parseCoord(data.lng),
      };
      let workshopId = id || null;
      workshopId = await saveWorkshop(database, userId, payload, workshopId);
      if (imageFile) {
        const url = await uploadWorkshopImage(storage, userId, workshopId, imageFile);
        if (url) await saveWorkshop(database, userId, { ...payload, image: url }, workshopId);
      }
      navigate('/workshops', { state: { userId } });
    } catch (e) {
      alert(t('update_failed', { defaultValue: 'No se pudo guardar' }));
      setSaving(false);
    }
  };

  const handleSolutionSave = async () => {
    if (!solution.title || !String(solution.title).trim()) {
      alert(t('workshop_solution_title_required', { defaultValue: 'El título del servicio es obligatorio' }));
      return;
    }
    try {
      await saveWorkshopSolution(database, userId, id, { ...solution, title: String(solution.title).trim() });
      setSolution({ solutionType: 'other', title: '', vehiclePlate: '', vehicleMake: '', cost: '', notes: '' });
      setShowSolutionForm(false);
    } catch (e) {
      alert(t('update_failed', { defaultValue: 'No se pudo guardar' }));
    }
  };

  if (loading) return <CircularLoading />;

  return (
    <ThemeProvider theme={theme}>
      <GoBackButton isRTL={isRTL} onClick={() => navigate('/workshops', { state: { userId } })} />
      <Card sx={{ p: 3, mt: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {isNew ? t('workshop_add', { defaultValue: 'Agregar taller' }) : t('workshop_edit', { defaultValue: 'Editar taller' })}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label={t('workshop_name', { defaultValue: 'Taller' })} value={data.name} onChange={handleChange('name')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('workshop_type', { defaultValue: 'Tipo' })}</InputLabel>
              <Select value={data.businessType} label={t('workshop_type', { defaultValue: 'Tipo' })} onChange={handleChange('businessType')}>
                {WORKSHOP_BUSINESS_TYPES.map((bt) => (
                  <MenuItem key={bt.id} value={bt.id}>{businessTypeLabel(bt.id, t)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label={t('mobile')} value={data.phone} onChange={handleChange('phone')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label={t('workshop_address', { defaultValue: 'Dirección' })} value={data.address} onChange={handleChange('address')} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={2} label={t('workshop_description', { defaultValue: 'Descripción' })} value={data.description} onChange={handleChange('description')} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth label={t('workshop_lat', { defaultValue: 'Latitud' })} value={data.lat} onChange={handleChange('lat')} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth label={t('workshop_lng', { defaultValue: 'Longitud' })} value={data.lng} onChange={handleChange('lng')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('workshop_services', { defaultValue: 'Servicios ofrecidos' })}</InputLabel>
              <Select
                multiple
                value={data.servicesOffered}
                label={t('workshop_services', { defaultValue: 'Servicios ofrecidos' })}
                onChange={handleChange('servicesOffered')}
                renderValue={(selected) => selected.map((s) => solutionTypeLabel(s, t)).join(', ')}
              >
                {VEHICLE_SOLUTION_TYPES.map((st) => (
                  <MenuItem key={st.id} value={st.id}>{solutionTypeLabel(st.id, t)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button variant="outlined" component="label" fullWidth sx={{ height: '100%' }}>
              {imageFile ? imageFile.name : t('workshop_image', { defaultValue: 'Foto del taller (opcional)' })}
              <input type="file" hidden accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={!!data.active} onChange={handleChange('active')} />}
              label={t('workshop_active', { defaultValue: 'Activo' })}
            />
          </Grid>
        </Grid>
        {(data.image && !imageFile) ? (
          <Box sx={{ mt: 2 }}>
            <img src={data.image} alt="" style={{ maxWidth: 220, borderRadius: 8 }} />
          </Box>
        ) : null}
        <Box sx={{ mt: 3 }}>
          <Button variant="contained" color="secondary" disabled={saving} onClick={handleSubmit}>
            {saving ? t('saving', { defaultValue: 'Guardando…' }) : t('submit')}
          </Button>
        </Box>
      </Card>

      {!isNew ? (
        <Card sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('workshop_solutions', { defaultValue: 'Servicios realizados' })}
          </Typography>
          <MaterialTable
            title=""
            columns={[
              { title: t('workshop_solution_title', { defaultValue: 'Servicio' }), field: 'title' },
              { title: t('workshop_solution_type', { defaultValue: 'Tipo' }), field: 'solutionType', render: (r) => solutionTypeLabel(r.solutionType, t) },
              { title: t('workshop_plate', { defaultValue: 'Placa' }), field: 'vehiclePlate' },
              { title: t('workshop_cost', { defaultValue: 'Costo' }), field: 'cost' },
            ]}
            data={solutions}
            style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr', boxShadow: 'none' }}
            options={{ ...TableStyle(settings), search: false, paging: solutions.length > 5 }}
            localization={localization(t)}
            editable={{
              onRowDelete: (oldData) => new Promise((resolve, reject) => {
                deleteWorkshopSolution(database, userId, id, oldData.id).then(resolve).catch(reject);
              }),
            }}
            actions={[
              {
                icon: 'add',
                tooltip: t('workshop_solution_add', { defaultValue: 'Agregar servicio' }),
                isFreeAction: true,
                onClick: () => setShowSolutionForm((v) => !v),
              },
            ]}
          />
          {showSolutionForm ? (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>{t('workshop_solution_type', { defaultValue: 'Tipo' })}</InputLabel>
                    <Select value={solution.solutionType} label={t('workshop_solution_type', { defaultValue: 'Tipo' })} onChange={(e) => setSolution((s) => ({ ...s, solutionType: e.target.value }))}>
                      {VEHICLE_SOLUTION_TYPES.map((st) => (
                        <MenuItem key={st.id} value={st.id}>{solutionTypeLabel(st.id, t)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField fullWidth label={t('workshop_solution_title', { defaultValue: 'Servicio' })} value={solution.title} onChange={(e) => setSolution((s) => ({ ...s, title: e.target.value }))} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth label={t('workshop_plate', { defaultValue: 'Placa' })} value={solution.vehiclePlate} onChange={(e) => setSolution((s) => ({ ...s, vehiclePlate: e.target.value }))} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth label={t('workshop_make', { defaultValue: 'Marca' })} value={solution.vehicleMake} onChange={(e) => setSolution((s) => ({ ...s, vehicleMake: e.target.value }))} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth label={t('workshop_cost', { defaultValue: 'Costo' })} value={solution.cost} onChange={(e) => setSolution((s) => ({ ...s, cost: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} label={t('workshop_notes', { defaultValue: 'Notas' })} value={solution.notes} onChange={(e) => setSolution((s) => ({ ...s, notes: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" color="secondary" onClick={handleSolutionSave}>{t('submit')}</Button>
                </Grid>
              </Grid>
            </Box>
          ) : null}
        </Card>
      ) : null}
    </ThemeProvider>
  );
}
